import { inject, Injectable } from '@angular/core';
import { AddressLineRequest, AddressLineResponse, CompanyDetails, CompanyDetailsResponse, CompanyPersonnel, CompanyPersonnelResponse, MeterDetailsRequest, MeterDetailsResponse } from '../models/registration.models';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { iif, map, Observable, of, switchMap, tap } from 'rxjs';
import { UtilService } from './util.service';
import { environment } from '../../environments/environment';
import { LoginService } from './login.service';

export interface RegistrationDetails {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  signature: string;
  mpanCount?: string;
  multimeterFile?: File;
  org?: string;
  orgId?: string;
  orgEmail?: string;
  mpan?: string;
  meterType?: 'smart' | 'hh';
  terms: boolean;
  dfsId?: string;
  sendWelcomeEmail?: boolean;
}

export interface WalletOnboardinDetails {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  groupId: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class RegisterService {

  endPointURL = environment.baseUrl;

  constructor(private http: HttpClient, private util: UtilService) { }

  getAddressLine(postCode: string): Observable<AddressLineResponse> {
    const URL = `${this.endPointURL}/meter/v3/address-line`;
    const payload: AddressLineRequest = {
      postCode,
      fuelType: 'elec',
      customerConsent: 'yes',
      customerName: 'BUSINESS UI'
    }
    // Implement logic to fetch address line from the server
    return this.http.post<AddressLineResponse>(URL, payload);
  }

  getMeterDetailsByAddressId(addressId: string): Observable<MeterDetailsResponse> {
    const URL = `${this.endPointURL}/meter/v3/meter-details-advanced`;
    const payload: MeterDetailsRequest = {
      addressId,
      fuelType: 'elec',
      customerConsent: 'yes',
    }
    // Implement logic to fetch meter details from the server
    return this.http.post<MeterDetailsResponse>(URL, payload);
  }

  getMeterDetailsByMPAN(mpan: string): Observable<MeterDetailsResponse> {
    const URL = `${this.endPointURL}/meter/v3/meter-details-advanced`;
    const payload: MeterDetailsRequest = {
      mpan,
      fuelType: 'elec',
      customerConsent: 'yes',
    }
    // Implement logic to fetch meter details from the server
    return this.http.post<MeterDetailsResponse>(URL, payload);
  }

  // searchCompany(searchQuery: string): Observable<CompanyDetailsResponse> {
  //   const URL = `${this.endPointURL}/carbon/v1/companies-by-text`;
  //   return this.http.get<CompanyDetailsResponse>(URL, { params: { searchQuery } });
  // }

   // API for company search
  searchCompany(query: string): Observable<CompanyDetails[]> {
    const url = `${this.endPointURL}/org/v1/search`;
    const params = {
      query
    };
    
    const headers = new HttpHeaders({
      'api_key': environment.apiKey
    });

    return this.http.get<CompanyDetailsResponse>(url, { 
      headers, 
      params 
    }).pipe(
      map(response => response.data)
    );
  }

  getCompanyPersonnel(companyNumber: string, companyType: string): Observable<CompanyPersonnel[]> {
    const url = `${this.endPointURL}/org/v1/contact`;
    const params: any = {
      number: companyNumber,
      type: companyType
    };
    
    const headers = new HttpHeaders({
      'api_key': environment.apiKey
    });
    return this.http.get<CompanyPersonnelResponse>(url, { 
      headers, 
      params 
    }).pipe(
      map(response => response.data)
    );
  }

  registerUser(regDetails: RegistrationDetails): Observable<any> {
    return iif(
      () => regDetails.mpanCount === 'multiple',
      this.uploadMultimeterFile(regDetails.multimeterFile!, regDetails),
      this.onBoardMeter(regDetails)
    ).pipe(
      tap((response) => {
        if (response.dfsId) {
          regDetails.dfsId = response.dfsId;
        }
      }),
      switchMap(() => this.onBoardWallet(regDetails)),
      switchMap(() => this.createUser(regDetails)),
    );
  }

  onBoardMeter(regDetails: RegistrationDetails): Observable<any> {
    const URL = `${this.endPointURL}/dfs/v1/dfs-onboard`;
    const meterOnboardingDetails = { ...regDetails, sendWelcomeEmail: true };
    return this.http.post<any>(URL, meterOnboardingDetails);
  }

  onBoardWallet(regDetails: RegistrationDetails): Observable<any> {
    const URL = `${this.endPointURL}/wallet/v1/onboard`;
    const walletOnboardingDetails: WalletOnboardinDetails = {
      firstname: regDetails.firstname,
      lastname: regDetails.lastname,
      email: regDetails.email,
      phone: regDetails.phone,
      groupId: null
    };
    return this.http.post<any>(URL, walletOnboardingDetails);
  }

  createUser(regDetails: RegistrationDetails): Observable<any> {
    const URL = `${this.endPointURL}/dfs/v1/create-user`;
    return this.http.post<any>(URL, {email: regDetails.email});
  }

  uploadMultimeterFile(file: File, regDetails: RegistrationDetails): Observable<any> {
    const URL = `${this.endPointURL}/dfs/v1/dfs-file-onboard`;
    const formData = new FormData();
    formData.append('InputFile', file);
    const params = new HttpParams()
      .set('serviceName', 'DFS')
      .set('email', regDetails.email)
      .set('firstname', regDetails.firstname)
      .set('lastname', regDetails.lastname)
      .set('phone', regDetails.phone)
      .set('signature', regDetails.signature);
    return this.http.post<any>(URL, formData, { params});
  }
}
